import { StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useEventLogging } from '../../../../../../models/Logging';
import { useClientCreateSatisfactionResponseMutation } from '../../../../../../queries';
import { useErrorMessagesContext } from '../../../../../components/ErrorModalProvider/context';
import { SatisfactionSurvey } from '../../components/SatisfactionSurvey';
import { IOperationResultNoValue, ISatisfactionSurvey, ISatisfactionSurveyResponse } from '../../../../../models/Api';
import { useSurvey } from '../../../../../models/hooks';
import { Survey } from '../../../shared/Survey';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const TakeSurvey = ({ className, styles = [] }: IProps) => {
	const {
		params: urlParams = {},
		path: matchPath,
		url,
	} = useRouteMatch<{
		token?: string;
	}>();
	const h = useHistory();
	const { logApiError } = useEventLogging('TakeSurvey');
	const { pushError } = useErrorMessagesContext();

	// #region Survey
	const { hasAccessToken, isUnavailable, surveyQuery } = useSurvey<ISatisfactionSurvey>({
		accessToken: urlParams.token,
		options: {
			expand: 'options',
		},
	});

	const surveyMutation = useClientCreateSatisfactionResponseMutation({});
	const { mutateAsync: createResponseAsync } = surveyMutation;

	React.useEffect(() => {
		if (!hasAccessToken) {
			return;
		}

		if (surveyQuery.error) {
			logApiError('Survey-Error', surveyQuery.error as IOperationResultNoValue);
			pushError(surveyQuery.error);
			return;
		}

		if (surveyQuery.data && isUnavailable) {
			h.push(`${matchPath.toLocaleLowerCase().replace(':token?', 'unavailable')}`);
		}
	}, [h, hasAccessToken, isUnavailable, logApiError, matchPath, pushError, surveyQuery.data, surveyQuery.error]);
	// #endregion

	const onSubmitResponse = React.useCallback(
		async (surveyResponse: ISatisfactionSurveyResponse) => {
			try {
				const response = await createResponseAsync({ accessToken: urlParams?.token, response: surveyResponse });
				h.push({
					pathname: `${url}/submitted`,
					state: {
						response,
						survey: surveyQuery.data,
					},
				});
			} catch (error) {
				if (error) {
					logApiError('SurveyResponse-Error', error);
					pushError(error);
					return;
				}
			}
		},
		[createResponseAsync, h, logApiError, pushError, surveyQuery.data, url, urlParams?.token]
	);

	const serveyContentStyles = React.useRef<StyleDeclarationValue[]>([styleSheet.surveyContent]).current;
	const renderContent = () => {
		if (!surveyQuery.data) {
			return null;
		}
		if (surveyQuery.data._type === 'SatisfactionSurvey') {
			return (
				<SatisfactionSurvey onRequestSubmit={onSubmitResponse} styles={serveyContentStyles} survey={surveyQuery.data} />
			);
		}
		return null;
	};

	return (
		<Survey
			survey={surveyQuery.data}
			className={`take-survey-container ${className || ''}`}
			styles={[styleSheet.container, ...styles]}
		>
			{renderContent()}
		</Survey>
	);
};
